class CartVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.selects = this.querySelectorAll('[data-option-index]');
    this.variantsJson = this.querySelector('[data-variants-json]');
    this.variants = [];
    this.currentVariantId = parseInt(this.dataset.variantId, 10);
    this.productId = this.dataset.productId;
    this.lineKey = this.dataset.lineKey;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this.parseVariants();
    this.attachEvents();
    this.updateSelectStates();
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  parseVariants() {
    if (this.variantsJson) {
      try {
        this.variants = JSON.parse(this.variantsJson.textContent);
      } catch (e) {
        console.error('Error parsing variants JSON:', e);
        this.variants = [];
      }
    }
  }

  attachEvents() {
    this.selects.forEach((select) => {
      select.addEventListener('change', this.handleOptionChange.bind(this), { signal: this._abortController.signal });
    });

    this.querySelectorAll('.cart-variant-selector__chevron').forEach((chevron) => {
      const select = chevron.closest('.cart-variant-selector__select-group')?.querySelector('select');
      if (!select) return;

      chevron.addEventListener('click', () => select.showPicker?.(), { signal: this._abortController.signal });
    });
  }

  handleOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const newVariant = this.findVariant(selectedOptions);

    if (newVariant && newVariant.id !== this.currentVariantId) {
      if (newVariant.available) {
        this.changeVariant(newVariant);
      } else {
        this.revertToCurrentVariant();
        this.showUnavailableMessage();
      }
    }

    this.updateSelectStates();
  }

  getSelectedOptions() {
    return Array.from(this.selects).map((select) => select.value);
  }

  findVariant(options) {
    return this.variants.find((variant) => {
      return variant.options.every((option, index) => option === options[index]);
    });
  }

  getCurrentVariant() {
    return this.variants.find((variant) => variant.id === this.currentVariantId);
  }

  revertToCurrentVariant() {
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.selects.forEach((select, index) => {
        select.value = currentVariant.options[index];
      });
    }
  }

  updateSelectStates() {
    const selectedOptions = this.getSelectedOptions();

    this.selects.forEach((select, selectIndex) => {
      const options = select.querySelectorAll('option');

      options.forEach((option) => {
        const testOptions = [...selectedOptions];
        testOptions[selectIndex] = option.value;
        const variant = this.findVariant(testOptions);

        if (variant && !variant.available) {
          option.disabled = true;
          if (!option.textContent.includes('—')) {
            option.textContent = `${option.value} — ${this.getSoldOutLabel()}`;
          }
        } else {
          option.disabled = false;
          option.textContent = option.value;
        }
      });
    });
  }

  getSoldOutLabel() {
    return theme.locales.cart.variantSoldOut;
  }

  async changeVariant(newVariant) {
    const lineItem = this.closest('[data-line-item-key]');
    const cartItems = this.closest('cart-items');

    if (!lineItem || !cartItems) return;

    this.setLoading(true);

    try {
      const quantity = this.getCurrentQuantity(lineItem);
      const cart = await this.updateCartVariant(newVariant.id, quantity);

      this.currentVariantId = newVariant.id;
      this.dataset.variantId = newVariant.id;

      this.updateLineItemUI(lineItem, newVariant);
      this.showUpdatedToast();
    } catch (error) {
      this.revertToCurrentVariant();
    } finally {
      this.setLoading(false);
    }
  }

  getCurrentQuantity(lineItem) {
    const input = lineItem.querySelector('[data-quantity-input]');
    return input ? parseInt(input.value, 10) : 1;
  }

  async updateCartVariant(newVariantId, quantity) {
    const sections = this.getSectionsToRender();

    theme.cart.errors.clearLine(this.lineKey);
    const lineItem = this.closest('[data-line-item-key]');
    const lineErrorSlot = lineItem?.querySelector('[data-line-error]');
    if (lineErrorSlot) { lineErrorSlot.hidden = true; lineErrorSlot.textContent = ''; }

    await theme.cart.change(this.lineKey, 0, { _internal: true, _silent: true, sectionsUrl: window.location.pathname });

    const cart = await theme.cart.add(
      { id: newVariantId, quantity },
      { sections, sectionsUrl: window.location.pathname, openDrawer: false, _internal: true }
    );

    this.lineKey = cart.items.find(item => item.variant_id === parseInt(newVariantId, 10))?.key || this.lineKey;
    this.dataset.lineKey = this.lineKey;

    return cart;
  }

  getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('cart-page');
    const sections = [];

    if (cartDrawer) {
      sections.push('cart-drawer');
    }
    if (cartPage) {
      sections.push('cart');
    }

    return sections.length > 0 ? sections : ['cart-drawer', 'cart'];
  }

  updateLineItemUI(lineItem, newVariant) {
    const priceElement = lineItem.querySelector('.cart-line-item__price-value');
    if (priceElement && newVariant.price) {
      priceElement.textContent = theme.utils.formatMoney(newVariant.price);
    }

    if (newVariant.featured_image) {
      const imageElement = lineItem.querySelector('.cart-line-item__thumbnail img');
      if (imageElement) {
        imageElement.src = newVariant.featured_image.src;
        imageElement.alt = newVariant.featured_image.alt;
      }
    }
  }

  showUpdatedToast() {
    if (!theme.settings?.cartActionToastsEnable) return;
    theme.toast?.show({ type: 'success', message: theme.locales?.cart?.success?.cartUpdated });
  }

  setLoading(isLoading) {
    const lineItem = this.closest('[data-line-item-key]');
    if (lineItem) {
      lineItem.classList.toggle('cart-line-item--loading', isLoading);
      const buttons = lineItem.querySelectorAll('button, select');
      buttons.forEach((el) => {
        el.disabled = isLoading;
      });
    }
  }

  showUnavailableMessage() {
    const message = theme.locales?.cart?.lineError?.cannotAdd || theme.locales?.cart?.variantUnavailable;
    theme.cart.errors.setLine(this.lineKey, message);
    theme.pubsub.publish(theme.pubsub.events.CART_ITEM_ERROR, {
      key: this.lineKey,
      message,
      type: 'cannot_add',
    });
  }
}

customElements.define('cart-variant-selector', CartVariantSelector);
